import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Divider, Drawer, Stack } from '@mui/material'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import TextFieldController from '@/components/inputs/TextFieldController'
import { ConfigField } from '@/features/controller/configuration/components/drawers/ConfigField'
import { DrawerActions } from '@/features/controller/configuration/components/drawers/DrawerActions'
import { DrawerDescription } from '@/features/controller/configuration/components/drawers/DrawerDescription'
import { DrawerTitle } from '@/features/controller/configuration/components/drawers/DrawerTitle'
import { IntegerConfigField } from '@/features/controller/configuration/components/drawers/IntegerConfigField'
import type { LegacyModbusTcpConnection } from '@/features/controller/configuration/models/raspiProperties/types'
import { getLegacyModbusTcpSchema } from '@/features/controller/configuration/schemas'
import type { ConfigDrawerProps, LegacyModbusTcpSchemaType } from '@/features/controller/configuration/types'

export function ModbusTcpConnectionDrawer({
  config,
  onClose,
  onConfigUpdated,
  onFinishedClosing,
  open,
}: Readonly<ConfigDrawerProps<LegacyModbusTcpConnection>>) {
  const { t } = useTranslation()
  const configPart = config.tcpTarget

  const form = useForm<LegacyModbusTcpSchemaType>({
    defaultValues: configPart,
    mode: 'onBlur',
    resolver: zodResolver(getLegacyModbusTcpSchema()),
  })

  const hasChanges = JSON.stringify(form.watch()) !== JSON.stringify(configPart)

  return (
    <Drawer
      anchor="right"
      open={open}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 580 },
        },
      }}
      onClose={onClose}
      onTransitionExited={onFinishedClosing}
    >
      <FormProvider {...form}>
        <DrawerTitle
          title={t('controllers.configuration.device_connections.modbus_tcp.dialog.title')}
          onClose={onClose}
        />

        <Divider />

        <DrawerDescription
          description={t('controllers.configuration.device_connections.modbus_tcp.dialog.description')}
        />

        {/* Content area */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
          }}
        >
          <Stack direction="column" padding={2} spacing={2}>
            <ConfigField label={t('controllers.configuration.device_connections.modbus_tcp.dialog.host')}>
              <TextFieldController name="host" type="text" />
            </ConfigField>
            <IntegerConfigField
              label={t('controllers.configuration.device_connections.modbus_tcp.dialog.port')}
              missingValue={0}
              name="port"
              textFieldProps={{
                slotProps: { htmlInput: { min: 1 } },
              }}
            />
          </Stack>
        </Box>

        <Divider />

        {/* Footer actions */}
        <DrawerActions
          hasChanges={hasChanges}
          onClose={onClose}
          onSave={(updatedConfig: LegacyModbusTcpSchemaType) =>
            onConfigUpdated({ ...config, tcpTarget: updatedConfig })
          }
        />
      </FormProvider>
    </Drawer>
  )
}
